"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth";

export function UserMenu({
  variant = "desktop",
  onNavigate,
}: {
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
}) {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const t = useTranslations("nav");
  const tDashboard = useTranslations("dashboard");

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKeyDown); };
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  if (loading) {
    return <div className={cn("rounded-full bg-ink-raised-2 animate-pulse", variant === "desktop" ? "h-9 w-24" : "h-10 w-full")} />;
  }

  if (!user) {
    return (
      <div className={cn("flex", variant === "desktop" ? "items-center gap-2" : "flex-col gap-1")}>
        <Link
          href="/submit-event"
          onClick={variant === "mobile" ? onNavigate : undefined}
          className={cn(
            "group inline-flex items-center justify-center gap-2 border border-green/45 bg-green/10 font-semibold text-green transition-all hover:border-green hover:bg-green hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green",
            variant === "desktop"
              ? "rounded-full px-3.5 py-2 text-sm shadow-[0_8px_22px_-16px_color-mix(in_oklab,var(--green)_75%,transparent)]"
              : "rounded-lg px-3 py-2.5 text-sm"
          )}
        >
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green text-sm leading-none text-white transition-colors group-hover:bg-white group-hover:text-green" aria-hidden="true">
            +
          </span>
          {t("createEvent")}
        </Link>
        <Link
          href="/signin"
          onClick={variant === "mobile" ? onNavigate : undefined}
          className={cn(
            "rounded-full bg-gold text-sm font-semibold text-ink transition-all hover:bg-gold-soft hover:shadow-[0_0_20px_color-mix(in_oklab,var(--gold)_50%,transparent)]",
            variant === "desktop" ? "px-4 py-2" : "block px-3 py-2.5 text-center"
          )}
        >
          {t("signIn")}
        </Link>
      </div>
    );
  }

  const initial = (user.name ?? user.email ?? "?").charAt(0).toUpperCase();
  const isAdmin = user.role === "admin";
  const isPromoter = user.role === "promoter";

  if (variant === "mobile") {
    return (
      <div className="flex flex-col gap-1">
        <Link href="/dashboard/tickets" onClick={onNavigate} className="rounded-lg px-3 py-2.5 text-sm font-medium text-ivory-muted hover:bg-ink-raised hover:text-ivory">
          {t("myTickets")}
        </Link>
        <Link href="/dashboard/events" onClick={onNavigate} className="rounded-lg px-3 py-2.5 text-sm font-medium text-ivory-muted hover:bg-ink-raised hover:text-ivory">
          {t("myEvents")}
        </Link>
        {isAdmin && (
          <Link href="/admin" onClick={onNavigate} className="rounded-lg px-3 py-2.5 text-sm font-medium text-ivory-muted hover:bg-ink-raised hover:text-ivory">
            Admin
          </Link>
        )}
        {isPromoter && (
          <Link href="/promoter" onClick={onNavigate} className="rounded-lg px-3 py-2.5 text-sm font-medium text-ivory-muted hover:bg-ink-raised hover:text-ivory">
            Promoter
          </Link>
        )}
        <Link href="/dashboard/following" onClick={onNavigate} className="rounded-lg px-3 py-2.5 text-sm font-medium text-ivory-muted hover:bg-ink-raised hover:text-ivory">
          {tDashboard("following")}
        </Link>
        <Link href="/dashboard/settings" onClick={onNavigate} className="rounded-lg px-3 py-2.5 text-sm font-medium text-ivory-muted hover:bg-ink-raised hover:text-ivory">
          {t("settings")}
        </Link>
        <button
          type="button"
          onClick={() => { logout(); window.location.assign("/"); }}
          className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-soft hover:bg-ink-raised"
        >
          {t("signOut")}
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative flex items-center gap-2">
      <Link
        href="/submit-event"
        className="group inline-flex items-center justify-center gap-2 rounded-full border border-green/45 bg-green/10 px-3.5 py-2 text-sm font-semibold text-green shadow-[0_8px_22px_-16px_color-mix(in_oklab,var(--green)_75%,transparent)] transition-all hover:border-green hover:bg-green hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green"
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green text-sm leading-none text-white transition-colors group-hover:bg-white group-hover:text-green" aria-hidden="true">
          +
        </span>
        {t("createEvent")}
      </Link>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open account menu"
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gold font-display text-sm font-semibold text-ink"
      >
        {initial}
      </button>
      {open && (
        <div role="menu" aria-label="Account menu" className="absolute top-full right-0 mt-2 w-52 overflow-hidden rounded-2xl border border-ink-border bg-ink-raised-2 py-1.5 shadow-2xl">
          <p className="truncate border-b border-ink-border/70 px-4 py-2.5 text-xs text-ivory-faint">{user.email}</p>
          <Link href="/dashboard/tickets" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-sm text-ivory hover:bg-surface">
            {t("myTickets")}
          </Link>
          <Link href="/dashboard/events" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-sm text-ivory hover:bg-surface">
            {t("myEvents")}
          </Link>
          {isAdmin && (
            <Link href="/admin" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-sm text-ivory hover:bg-surface">
              Admin
            </Link>
          )}
          {isPromoter && (
            <Link href="/promoter" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-sm text-ivory hover:bg-surface">
              Promoter
            </Link>
          )}
          <Link href="/dashboard/following" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-sm text-ivory hover:bg-surface">
            {tDashboard("following")}
          </Link>
          <Link href="/dashboard/settings" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-sm text-ivory hover:bg-surface">
            {t("settings")}
          </Link>
          <button
            type="button"
            onClick={() => { logout(); window.location.assign("/"); }}
            className="block w-full px-4 py-2.5 text-left text-sm text-red-soft hover:bg-surface"
          >
            {t("signOut")}
          </button>
        </div>
      )}
    </div>
  );
}
